import React, { useState } from 'react';
import { AvatarEditorProps } from 'react-avatar-editor';
import { useImageUpload } from '.';
import { Button, ImageEditorModal } from '../../UIKit';
import { makeStyles } from '~/UIKit/Style';

export type UploadedImage = {
  id: string;
  path: string;
  url: string;
};

export interface IImageUploadProps {
  disabled?: boolean;
  canvasStyle: Partial<AvatarEditorProps>;
  onUpload?: (imageDataUrl: string) => void;
  className?: string;
  actionButtonIcon?: React.ReactElement;
  buttonPrimaryLabel?: string;
  buttonSecondaryLabel?: string;
  restrictedToUSCTypes?: boolean;
}

const useStyles = makeStyles({
  fileInput: {
    display: 'none',
  },
});

export const ImageUpload = ({
  disabled,
  canvasStyle,
  className = '',
  onUpload,
  actionButtonIcon,
  buttonPrimaryLabel,
  buttonSecondaryLabel,
  restrictedToUSCTypes = false,
}: IImageUploadProps) => {
  const ACCEPTED_IMAGE_TYPES = restrictedToUSCTypes
    ? 'image/gif, image/jpeg, image/jpg, image/png, image/vnd.wap.wbmp, image/xbm'
    : ' image/*';

  const {
    handleImageFileChanged,
    handleImageUpload,
    imageUploading,
    t,
    setImageSrc,
    imageSrc,
    file,
  } = useImageUpload();
  const { classes } = useStyles();
  const [isUploading, setIsUploading] = useState(false);

  const onImageUpload = async (imageDataUrl: string) => {
    // if onUpload function is overridden
    if (onUpload) {
      setIsUploading(true);
      await onUpload(imageDataUrl);
      setIsUploading(false);
      setImageSrc(undefined);
      return;
    }
    // default behavior of onUpload function
    handleImageUpload(imageDataUrl);
  };

  const textLabel = file
    ? buttonSecondaryLabel ?? t('app.changePicture')
    : buttonPrimaryLabel ?? t('app.uploadImage');

  return (
    <>
      <input
        accept={ACCEPTED_IMAGE_TYPES}
        className={classes.fileInput}
        data-testid="ImageUploadButton"
        id="contained-button-file"
        onChange={handleImageFileChanged}
        disabled={disabled}
        type="file"
      />
      <label htmlFor="contained-button-file">
        <Button
          color="primary"
          component="span"
          variant="outlined"
          size="medium"
          className={className}
          isLoading={imageUploading}
          disabled={disabled}
        >
          {actionButtonIcon || textLabel}
        </Button>
      </label>
      {file && (
        <ImageEditorModal
          originalFile={file}
          imageSrc={imageSrc}
          onUpload={onImageUpload}
          onClose={() => setImageSrc(undefined)}
          isLoading={imageUploading || isUploading}
          canvasStyle={canvasStyle}
          open={Boolean(imageSrc)}
        />
      )}
    </>
  );
};
